import {
  createContext,
  Suspense,
  useContext,
  useEffect,
  useMemo,
  useSyncExternalStore,
  type ReactNode,
  type ComponentType,
} from "react";
import { modalStore } from "@/lib/modal/store";
import type {
  ModalComponentProps,
  ModalEventClosePayload,
  ModalEventPushPayload,
  ModalId,
} from "@/lib/modal/types";
import {
  ResponsiveModal,
  ResponsiveModalContent,
} from "@/components/ui/responsive-modal";

interface ModalContextValue {
  pushModal: <TProps extends Record<string, unknown>>(
    component: ComponentType<TProps & ModalComponentProps>,
    props: TProps
  ) => ModalId;
  popModal: (modalId?: ModalId) => void;
  popAllModals: () => void;
  replaceWithModal: <TProps extends Record<string, unknown>>(
    component: ComponentType<TProps & ModalComponentProps>,
    props: TProps
  ) => ModalId;
  onPushModal: (
    listener: (payload: ModalEventPushPayload) => void
  ) => () => void;
  onCloseModal: (
    listener: (payload: ModalEventClosePayload) => void
  ) => () => void;
}

const ModalContext = createContext<ModalContextValue | null>(null);

interface ModalProviderProps {
  children: ReactNode;
}

export function ModalProvider({ children }: ModalProviderProps) {
  const state = useSyncExternalStore(
    modalStore.subscribe,
    modalStore.getState,
    modalStore.getState
  );

  const contextValue = useMemo<ModalContextValue>(
    () => ({
      pushModal: modalStore.pushModal,
      popModal: modalStore.popModal,
      popAllModals: modalStore.popAllModals,
      replaceWithModal: modalStore.replaceWithModal,
      onPushModal: modalStore.onPushModal,
      onCloseModal: modalStore.onCloseModal,
    }),
    []
  );

  return (
    <ModalContext.Provider value={contextValue}>
      {children}
      {state.modals.map((modal) => {
        const Component = modal.component;
        const contentClassName = (
          modal.component as ComponentType & {
            contentClassName?: string;
          }
        ).contentClassName;
        return (
          <ResponsiveModal
            key={modal.id}
            open={!modal.isClosing}
            onOpenChange={(open) => {
              if (!open && !modal.isClosing) modalStore.popModal(modal.id);
            }}
          >
            <ResponsiveModalContent
              className={contentClassName}
              skipOverlayAnimation={modal.skipOverlayAnimation}
            >
              <Suspense>
                <div
                  className={modal.isClosing ? "invisible min-w-0" : "min-w-0"}
                >
                  <Component
                    {...modal.props}
                    onClose={() => modalStore.popModal(modal.id)}
                  />
                </div>
              </Suspense>
            </ResponsiveModalContent>
          </ResponsiveModal>
        );
      })}
    </ModalContext.Provider>
  );
}

// eslint-disable-next-line react-refresh/only-export-components
export function useModalContext(): ModalContextValue {
  const context = useContext(ModalContext);
  if (!context) {
    throw new Error("useModalContext must be used within a ModalProvider");
  }
  return context;
}

// eslint-disable-next-line react-refresh/only-export-components
export function useModal() {
  return useModalContext();
}

// eslint-disable-next-line react-refresh/only-export-components
export function useModalEvents(handlers: {
  onPushModal?: (payload: ModalEventPushPayload) => void;
  onCloseModal?: (payload: ModalEventClosePayload) => void;
}) {
  const { onPushModal, onCloseModal } = useModalContext();

  useEffect(() => {
    const unsubscribers: (() => void)[] = [];

    if (handlers.onPushModal) {
      unsubscribers.push(onPushModal(handlers.onPushModal));
    }
    if (handlers.onCloseModal) {
      unsubscribers.push(onCloseModal(handlers.onCloseModal));
    }

    return () => {
      unsubscribers.forEach((unsubscribe) => unsubscribe());
    };
  }, [handlers.onPushModal, handlers.onCloseModal, onPushModal, onCloseModal]);
}
